import GenericTable from "components/table";
import { Suggestion, suggestionsApi, SuggestionType } from "api/suggestions";
import React, { useState } from "react";
import { Trash } from "lucide-react";
import { useConfirmation } from "components/dialog/confirmation";
import { useAuthContext } from "auth/AuthProvider";
import { handleAxiosAndCaughtError } from "utils";
import { CellContext } from "@tanstack/react-table";
import { PERMISSIONS } from "auth/permissions";
import { DATE_TIME_FORMAT } from "constants/date";
import dayjs from "dayjs";
import EditSuggestionForm from "./EditSuggestionForm";
import { Dialog } from "components/dialog";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

type SuggestionsTableProps = {
  tableData: Suggestion[];
  fetchSuggestions: () => Promise<void>;
  agentId?: string;
  suggestionType: SuggestionType;
};

const SuggestionsDataTable: React.FC<SuggestionsTableProps> = ({
  tableData,
  fetchSuggestions,
  agentId,
  suggestionType,
}) => {
  const { t } = useTranslation();
  const [deletingRowId, setDeletingRowId] = React.useState<string | null>(null);
  const { hasPermission } = useAuthContext();
  const { confirm } = useConfirmation();

  const permissions = {
    canViewSuggestions:
      hasPermission(PERMISSIONS.VIEW_SUGGESTIONS) ||
      hasPermission(PERMISSIONS.SUPER_ADMIN),
    canCreateSuggestions:
      hasPermission(PERMISSIONS.CREATE_SUGGESTIONS) ||
      hasPermission(PERMISSIONS.SUPER_ADMIN),
    canUpdateSuggestions:
      hasPermission(PERMISSIONS.UPDATE_SUGGESTIONS) ||
      hasPermission(PERMISSIONS.SUPER_ADMIN),
    canDeleteSuggestions:
      hasPermission(PERMISSIONS.DELETE_SUGGESTIONS) ||
      hasPermission(PERMISSIONS.SUPER_ADMIN),
  };

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<Suggestion | null>(null);

  const handleDeleteSuggestion = async (suggestionId: string) => {
    if (!agentId) return;

    if (!permissions.canDeleteSuggestions) {
      toast.error(t("suggestions.noPermissionToDelete"));
      return;
    }

    const confirmed = await confirm({
      title: t("suggestions.deleteSuggestion"),
      message: t("suggestions.deleteSuggestionConfirm"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      setDeletingRowId(suggestionId);
      try {
        await suggestionsApi.delete(agentId, suggestionId);
        await fetchSuggestions();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setDeletingRowId(null);
      }
    }
  };

  const handleEditSuggestion = (suggestion: Suggestion) => {
    setSelectedSuggestion(suggestion);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditDialogOpen(false);
    setSelectedSuggestion(null);
  };

  const columns = [
    {
      header: t("suggestions.content"),
      accessorKey: "suggestion_content",
      cell: ({ row }: CellContext<Suggestion, any>) => (
        <div className="max-w-md">
          <span className="line-clamp-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            {row.original.suggestion_content}
          </span>
        </div>
      ),
    },
    {
      header: t("library.createdDate"),
      accessorKey: "created_at",
      cell: ({ row }: CellContext<Suggestion, any>) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {dayjs(row.original.created_at).format(DATE_TIME_FORMAT)}
        </span>
      ),
    },
    {
      header: t("library.updatedDate"),
      accessorKey: "updated_at",
      cell: ({ row }: CellContext<Suggestion, any>) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {dayjs(row.original.updated_at).format(DATE_TIME_FORMAT)}
        </span>
      ),
    },
    {
      header: t("library.actions"),
      accessorKey: "actions",
      cell: ({ row }: CellContext<Suggestion, any>) => (
        <div className="flex items-center gap-2">
          {permissions.canDeleteSuggestions && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSuggestion(row.original.id);
              }}
              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
              title={t("suggestions.deleteSuggestion")}
              disabled={deletingRowId === row.original.id}
            >
              <Trash className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return deletingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  return (
    <div className="space-y-4">
      <GenericTable
        data={tableData}
        columns={columns}
        searchPlaceholder={t("suggestions.searchSuggestions", {
          type:
            suggestionType === SuggestionType.INITIALIZE
              ? t("suggestions.autoGenerated").toLowerCase()
              : t("suggestions.frequent").toLowerCase(),
        })}
        onAdd={undefined}
        onRowClick={
          permissions.canUpdateSuggestions
            ? (item) => {
                handleEditSuggestion(item);
              }
            : undefined
        }
        renderRowClassName={renderRowClassName}
        addLabel={undefined}
      />

      {isEditDialogOpen && selectedSuggestion && (
        <Dialog isOpen={isEditDialogOpen} setIsOpen={handleCloseEditModal}>
          <EditSuggestionForm
            onClose={handleCloseEditModal}
            onSuccess={fetchSuggestions}
            suggestion={selectedSuggestion}
            agentId={agentId}
          />
        </Dialog>
      )}
    </div>
  );
};

export default SuggestionsDataTable;
